import { useRouteLoaderData } from "@remix-run/react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SHOW_CONFIRM_ON_AUTO_ADVANCE_STEPS } from "~/components/booking/flow/confirmButton";
import { ServiceCardSubtitle } from "~/components/booking/flow/ServiceCardExtras";
import { Button } from "~/components/ui/Button";
import { VOUCHER_PACKAGE_ALTERNATIVES_SELECTABLE } from "~/lib/booking-voucher-packages";
import { keepAnimationOnMainThread } from "~/lib/motion";
import { sortByWpServiceOrder } from "~/lib/wp-order";
import {
  cn,
  getCmsServiceCategoryFromList,
  getCmsServiceFromList,
} from "~/lib/utils";
import { useBookingCart as useBookingCartStore } from "~/stores/bookingCartContext";

interface PackageStepProps {
  disableConfirm?: boolean;
  onConfirm: (packageData: any) => void;
  onCancel: () => void;
  selectedDate: Date;
  guestsCount: { male: number; female: number };
  selectedPackage?: any;
  selectedPackages?: any[];
  basketItems?: any[];
  loading?: boolean;
  isVoucherBooking?: boolean;
}

const PackageStep = ({
  disableConfirm = false,
  onConfirm,
  onCancel,
  selectedDate,
  guestsCount,
  selectedPackage,
  selectedPackages,
  basketItems = [],
  loading = false,
  isVoucherBooking = false,
}: PackageStepProps) => {
  const { t } = useTranslation();
  const rootData = useRouteLoaderData<any>("root");
  const { bookingCart } = useBookingCartStore();
  const [currentStepNr, setCurrentStepNr] = useState<number>(1);
  const [selectedTimes, setSelectedTimes] = useState<Record<number, any>>({});
  const [packageComposition, setPackageComposition] = useState<any[]>([]);
  const [availableServices, setAvailableServices] = useState<
    Record<number, any[]>
  >({});
  const [entranceTime, setEntranceTime] = useState<any>(null);
  const [hasErrors, setHasErrors] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sessionRetryAttempted, setSessionRetryAttempted] = useState(false);
  const [isSkippingStep, setIsSkippingStep] = useState(false);
  const [direction, setDirection] = useState(0);
  const [isLoadingInitialAvailability, setIsLoadingInitialAvailability] =
    useState(false);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const fetchedStepsRef = useRef<Set<number>>(new Set());
  const [selectedServiceForStep, setSelectedServiceForStep] = useState<
    Record<number, string>
  >({});

  const numPaxReq = guestsCount.male + guestsCount.female;

  const determineSelectedPackage = () => {
    const packagesToUse =
      selectedPackages && selectedPackages.length > 0
        ? selectedPackages
        : selectedPackage
        ? [selectedPackage]
        : [];

    if (packagesToUse.length === 1) {
      return packagesToUse[0];
    }

    if (packagesToUse.length === 0) {
      return selectedPackage;
    }

    const selectedServiceCodes = new Set<string>();
    Object.values(selectedTimes).forEach((time: any) => {
      if (time && time.ServiceCode) {
        selectedServiceCodes.add(time.ServiceCode);
      }
    });
    if (entranceTime && entranceTime.ServiceCode) {
      selectedServiceCodes.add(entranceTime.ServiceCode);
    }

    for (const pkg of packagesToUse) {
      const pkgServiceCodes = new Set<string>();
      const packageComposition =
        pkg.apiService?.PackageComposition || pkg.PackageComposition;
      packageComposition?.forEach((step: any) => {
        step.ServicesList?.forEach((service: any) => {
          pkgServiceCodes.add(service.Code);
        });
      });

      const allSelectedInPackage = Array.from(selectedServiceCodes).every(
        (code) => pkgServiceCodes.has(code),
      );

      if (allSelectedInPackage && selectedServiceCodes.size > 0) {
        const allPackageServicesSelected = Array.from(pkgServiceCodes).every(
          (code) => selectedServiceCodes.has(code) || !code,
        );

        if (
          allPackageServicesSelected ||
          selectedServiceCodes.size === pkgServiceCodes.size
        ) {
          return pkg;
        }
      }
    }

    return packagesToUse[0];
  };

  const unifyPackageCompositions = (packages: any[]) => {
    if (packages.length === 1) {
      const stepMap = new Map<number, any>();
      const pkg = packages[0];
      const packageComposition =
        pkg.apiService?.PackageComposition || pkg.PackageComposition;

      packageComposition?.forEach((step: any) => {
        const stepNr = step.StepNr;
        if (!stepMap.has(stepNr)) {
          stepMap.set(stepNr, {
            StepNr: stepNr,
            StepCategory: step.StepCategory,
            NrRequested: step.NrRequested,
            ServicesList: [],
          });
        }

        const unifiedStep = stepMap.get(stepNr);
        step.ServicesList?.forEach((service: any) => {
          const existingService = unifiedStep.ServicesList.find(
            (s: any) => s.Code === service.Code,
          );
          if (!existingService) {
            unifiedStep.ServicesList.push(service);
          }
        });
      });

      return Array.from(stepMap.values()).sort((a, b) => a.StepNr - b.StepNr);
    }

    const entranceStepMap = new Map<number, any>();
    const categoryServicesMap = new Map<string, any>();

    packages.forEach((pkg) => {
      const packageComposition =
        pkg.apiService?.PackageComposition || pkg.PackageComposition;

      if (!packageComposition || !Array.isArray(packageComposition)) {
        console.warn("Package without PackageComposition:", pkg);
        return;
      }

      packageComposition.forEach((step: any) => {
        if (!step || !step.ServicesList || !Array.isArray(step.ServicesList)) {
          console.warn("Step without ServicesList:", step);
          return;
        }

        if (step.StepNr === 1) {
          if (!entranceStepMap.has(1)) {
            entranceStepMap.set(1, {
              StepNr: 1,
              StepCategory: step.StepCategory,
              NrRequested: step.NrRequested,
              ServicesList: [],
            });
          }
          const entranceStep = entranceStepMap.get(1);
          step.ServicesList.forEach((service: any) => {
            if (!service || !service.Code) {
              return;
            }
            const existingService = entranceStep.ServicesList.find(
              (s: any) => s.Code === service.Code,
            );
            if (!existingService) {
              entranceStep.ServicesList.push(service);
            }
          });
        } else {
          const stepCategory = step.StepCategory || "DEFAULT_CATEGORY";

          if (!categoryServicesMap.has(stepCategory)) {
            categoryServicesMap.set(stepCategory, {
              StepCategory: stepCategory,
              NrRequested: 1,
              ServicesList: [],
            });
          }

          const categoryStep = categoryServicesMap.get(stepCategory);
          step.ServicesList.forEach((service: any) => {
            if (!service || !service.Code) {
              return;
            }
            const existingService = categoryStep.ServicesList.find(
              (s: any) => s.Code === service.Code,
            );
            if (!existingService) {
              categoryStep.ServicesList.push(service);
            }
          });
        }
      });
    });

    const result: any[] = [];

    if (entranceStepMap.has(1)) {
      result.push(entranceStepMap.get(1));
    }

    const categorySteps = Array.from(categoryServicesMap.values());
    categorySteps.forEach((categoryStep) => {
      result.push({
        StepNr: result.length + 1,
        StepCategory: categoryStep.StepCategory,
        NrRequested: 1,
        ServicesList: categoryStep.ServicesList,
      });
    });

    return result;
  };

  useEffect(() => {
    const packagesToUse =
      selectedPackages && selectedPackages.length > 0
        ? selectedPackages
        : selectedPackage
        ? [selectedPackage]
        : [];

    if (packagesToUse.length > 0) {
      const unifiedComposition = unifyPackageCompositions(packagesToUse);

      if (unifiedComposition.length === 0) {
        console.error("unifiedComposition is empty!");
        return;
      }

      setPackageComposition(unifiedComposition);
      setCurrentStepNr(1);
      setIsSkippingStep(false);
      setEntranceTime(null);
      setSelectedTimes({});
      setAvailableServices({});
      setSelectedServiceForStep({});
      fetchedStepsRef.current.clear();

      const treatmentSteps = unifiedComposition.filter((s) => s.StepNr > 1);
      const allStepsAreUnique = treatmentSteps.every(
        (step) => (step.NrRequested || 1) === 1,
      );
    }
  }, [selectedPackage, selectedPackages]);

  useEffect(() => {
    setIsSkippingStep(false);
    fetchedStepsRef.current.delete(currentStepNr);
  }, [currentStepNr]);

  useEffect(() => {
    if (packageComposition.length > 0 && currentStepNr > 0) {
      const step = packageComposition.find((s) => s.StepNr === currentStepNr);
      if (!step) return;

      if (step?.ServicesList && step.ServicesList.length > 0) {
        const servicesHaveAvailability = step.ServicesList.some(
          (s: any) =>
            s.Availability &&
            Array.isArray(s.Availability) &&
            s.Availability.length > 0,
        );

        if (servicesHaveAvailability) {
          if (!fetchedStepsRef.current.has(currentStepNr)) {
            let filteredServices = step.ServicesList;

            if (currentStepNr > 1) {
              if (!entranceTime) {
                return;
              }
              filteredServices = filterServicesFromPackageComposition(
                step.ServicesList,
              );
            }

            setAvailableServices((prev) => ({
              ...prev,
              [currentStepNr]: filteredServices,
            }));
            setIsLoadingInitialAvailability(false);
            setIsLoadingServices(false);
            fetchedStepsRef.current.add(currentStepNr);
          }
          return;
        }
      }

      if (currentStepNr === 1) {
        if (step?.ServicesList && step.ServicesList.length > 0) {
          if (!isLoadingServices && !fetchedStepsRef.current.has(1)) {
            setIsLoadingInitialAvailability(true);
            fetchServicesForStep(1);
            fetchedStepsRef.current.add(1);
          }
        }
      } else {
        if (!entranceTime) {
          console.warn(
            "PackageStep: Cannot load services for step",
            currentStepNr,
            "- entranceTime not set",
          );
          return;
        }

        if (step?.ServicesList && step.ServicesList.length > 0) {
          if (
            !isLoadingServices &&
            !fetchedStepsRef.current.has(currentStepNr)
          ) {
            fetchServicesForStep(currentStepNr);
            fetchedStepsRef.current.add(currentStepNr);
          }
        }
      }
    }
  }, [currentStepNr, packageComposition, entranceTime]);

  const fetchServicesForStep = async (stepNr: number) => {
    setHasErrors(false);
    setErrorMessage(null);
    setIsLoadingServices(true);

    const step = packageComposition.find((s) => s.StepNr === stepNr);
    if (!step) {
      setIsLoadingServices(false);
      return;
    }

    const formattedDate = selectedDate.toISOString().split("T")[0];
    let serviceCodes = step.ServicesList?.map((s: any) => s.Code) || [];

    const packagesToUse =
      selectedPackages && selectedPackages.length > 0
        ? selectedPackages
        : selectedPackage
        ? [selectedPackage]
        : [];

    if (isVoucherBooking && stepNr > 1 && packagesToUse.length > 1) {
      const allStepServices = new Set<string>();
      packagesToUse.forEach((pkg: any) => {
        const packageComposition =
          pkg.apiService?.PackageComposition || pkg.PackageComposition;
        const pkgStep = packageComposition?.find(
          (s: any) => s.StepNr === stepNr,
        );
        if (pkgStep?.ServicesList) {
          pkgStep.ServicesList.forEach((s: any) => {
            allStepServices.add(s.Code);
          });
        }
      });
      serviceCodes = Array.from(allStepServices);
    } else if (
      isVoucherBooking &&
      VOUCHER_PACKAGE_ALTERNATIVES_SELECTABLE &&
      stepNr > 1
    ) {
      // Legacy widening: adds to the step the voucher codes that are not part
      // of its own `ServicesList`. It existed to offer the alternative
      // durations; with the choice disabled the step trusts only its own
      // composition, otherwise the treatment step can show codes belonging to
      // other steps (the entrance) or the packages themselves.
      const vouchers = bookingCart?.vouchers || [];
      const allIncludedCodes = vouchers.reduce((acc: string[], v) => {
        const codes = v.voucherData?.ServicesIncludedCodes || [];
        return [...acc, ...codes];
      }, []);
      const voucherServicesIncluded = Array.from(new Set(allIncludedCodes));

      const stepServiceCodesSet = new Set(serviceCodes);
      const additionalVoucherServices = voucherServicesIncluded.filter(
        (code) => !stepServiceCodesSet.has(code),
      );

      if (additionalVoucherServices.length > 0) {
        serviceCodes = [...serviceCodes, ...additionalVoucherServices];
      }
    }

    if (serviceCodes.length === 0) {
      setAvailableServices((prev) => ({
        ...prev,
        [stepNr]: [],
      }));
      fetchedStepsRef.current.add(stepNr);
      setIsLoadingServices(false);
      return;
    }

    const currentBasketItems = buildBasketItemsForSearch();
    const vouchers = isVoucherBooking ? bookingCart?.vouchers || [] : [];

    const uniqueServiceCodes = [...new Set(serviceCodes)];
    let url = `/api/booking/availability/hours?checkinDate=${formattedDate}&men=${
      guestsCount.male
    }&women=${guestsCount.female}&services=${uniqueServiceCodes.join(",")}`;

    if (isVoucherBooking && vouchers.length > 0) {
      const vouchersPayload = vouchers.map((v) => ({
        VoucherCode: v.code,
        GiftCardType: v.giftCardType || "",
      }));
      url += `&vouchers=${encodeURIComponent(JSON.stringify(vouchersPayload))}`;
    }

    try {
      let response: Response;

      if (currentBasketItems.length > 0) {
        const body: any = { BasketItems: currentBasketItems };
        if (isVoucherBooking && vouchers.length > 0) {
          body.Vouchers = vouchers.map((v: any) => ({
            VoucherCode: v.code,
            GiftCardType: v.giftCardType,
          }));
        }
        response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });
      } else {
        response = await fetch(url);
      }

      const data: any = await response.json();
      setIsLoadingInitialAvailability(false);
      setIsLoadingServices(false);

      if (data.error) {
        if (data.error === "max_pax_exceeded") {
          setHasErrors(true);
          if (data.maxPax) {
            setErrorMessage(
              t(
                "Al momento accettiamo al massimo prenotazioni per {{maxPax}} persone",
                {
                  maxPax: data.maxPax,
                },
              ),
            );
          } else {
            setErrorMessage(
              t(
                "Al momento accettiamo un numero limitato di persone per prenotazione",
              ),
            );
          }
        } else if (data.error === "max_man_pax_exceeded") {
          setHasErrors(true);
          if (data.maxManPax) {
            setErrorMessage(
              t(
                "Al momento accettiamo al massimo prenotazioni per {{maxManPax}} uomini",
                {
                  maxManPax: data.maxManPax,
                },
              ),
            );
          } else {
            setHasErrors(true);
          }
        } else if (data.error === "max_woman_pax_exceeded") {
          setHasErrors(true);
          if (data.maxWomanPax) {
            setErrorMessage(
              t(
                "Al momento accettiamo al massimo prenotazioni per {{maxWomanPax}} donne",
                {
                  maxWomanPax: data.maxWomanPax,
                },
              ),
            );
          } else {
            setHasErrors(true);
          }
        } else {
          setHasErrors(true);
          setErrorMessage(null);
        }
        return;
      }

      if (step && data) {
        const services = Array.isArray(data) ? data : [];

        const filteredServices = filterServicesForStep(services, step);

        setAvailableServices((prev) => ({
          ...prev,
          [stepNr]: filteredServices,
        }));
        setSessionRetryAttempted(false);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
      setIsLoadingServices(false);
      setIsLoadingInitialAvailability(false);
      setHasErrors(true);
      setErrorMessage(
        t("Qualcosa è andato storto! Ricarica la pagina e riprova"),
      );
    }
  };

  const buildBasketItemsForSearch = () => {
    const items: any[] = [];

    if (entranceTime) {
      const entranceStep = packageComposition.find((s) => s.StepNr === 1);
      if (entranceStep?.ServicesList) {
        const entranceService = entranceStep.ServicesList.find((s: any) =>
          s.Availability?.some(
            (a: any) =>
              a.StartTime === entranceTime.StartTime &&
              a.EndTime === entranceTime.EndTime,
          ),
        );

        if (entranceService) {
          for (let i = 0; i < numPaxReq; i++) {
            const isFemale = i >= guestsCount.male;
            items.push({
              ServiceType: entranceService.Type || "Ticket",
              Type: "Service",
              Service: entranceService.Code,
              CheckinDate: selectedDate.toISOString().split("T")[0],
              StartTime: entranceTime.StartTime,
              EndTime: entranceTime.EndTime,
              GrossPrice: {
                Price: 0,
                VatIncluded: true,
                Currency: "EUR",
              },
              Role: "INCLUDED",
              Quantity: {
                Total: 1,
                Man: isFemale ? 0 : 1,
                Woman: isFemale ? 1 : 0,
                ThirdType: 0,
              },
            });
          }
        }
      }
    }

    Object.values(selectedTimes).forEach((time) => {
      if (time) {
        items.push({
          ServiceType: time.ServiceType || "Treatment",
          Type: "Service",
          Service: time.ServiceCode,
          CheckinDate: selectedDate.toISOString().split("T")[0],
          StartTime: time.StartTime,
          EndTime: time.EndTime,
          GrossPrice: {
            Price: 0,
            VatIncluded: true,
            Currency: "EUR",
          },
          Role: "INCLUDED",
          Quantity: {
            Total: 1,
            Man: 0,
            Woman: 0,
            ThirdType: 0,
          },
        });
      }
    });

    return items;
  };

  const filterServicesFromPackageComposition = (servicesList: any[]) => {
    if (!entranceTime) {
      return [];
    }

    const filtered: any[] = [];

    servicesList.forEach((service) => {
      if (!service.Availability || service.Availability.length === 0) {
        return;
      }

      const availableTimes = service.Availability.filter((avail: any) => {
        if (avail.Quantity?.Total === 0) return false;

        const startTime = avail.StartTime;
        const endTime = avail.EndTime;

        if (startTime === "00:00:00" && endTime === "23:59:59") {
          return true;
        }

        const entranceStart = entranceTime.StartTime;
        const entranceEnd = entranceTime.EndTime;
        const entranceHideEnd = entranceTime.HideEndTime || false;

        const startTimeObj = new Date(`2000-01-01T${startTime}`);
        const entranceStartObj = new Date(`2000-01-01T${entranceStart}`);

        if (startTimeObj <= entranceStartObj) {
          return false;
        }

        if (!entranceHideEnd) {
          const endTimeObj = new Date(`2000-01-01T${endTime}`);
          const entranceEndObj = new Date(`2000-01-01T${entranceEnd}`);
          if (endTimeObj >= entranceEndObj) {
            return true;
          }
        }

        return true;
      });

      if (availableTimes.length > 0) {
        filtered.push({
          ...service,
          Availability: availableTimes,
        });
      }
    });

    return filtered;
  };

  const filterServicesForStep = (services: any[], step: any) => {
    if (currentStepNr === 1) {
      return services;
    }

    if (!entranceTime) {
      return [];
    }

    const filtered: any[] = [];

    services.forEach((service) => {
      if (!service.Availability || service.Availability.length === 0) {
        return;
      }

      let stepService = step.ServicesList?.find(
        (s: any) => s.Code === service.Code,
      );

      // Counterpart of the widening in `fetchServicesForStep`: accepts a service
      // that is NOT in the step composition as long as the voucher covers it.
      // Only applies while the choice between alternatives is enabled.
      if (
        !stepService &&
        isVoucherBooking &&
        VOUCHER_PACKAGE_ALTERNATIVES_SELECTABLE
      ) {
        const vouchers = bookingCart?.vouchers || [];
        const allIncludedCodes = vouchers.reduce((acc: string[], v) => {
          const codes = v.voucherData?.ServicesIncludedCodes || [];
          return [...acc, ...codes];
        }, []);
        const voucherServicesIncluded = Array.from(new Set(allIncludedCodes));

        if (voucherServicesIncluded.includes(service.Code)) {
          stepService = { Code: service.Code };
        }
      }

      if (!stepService) {
        return;
      }

      const availableTimes = service.Availability.filter((avail: any) => {
        if (avail.Quantity?.Total === 0) return false;

        const startTime = avail.StartTime;
        const endTime = avail.EndTime;

        if (startTime === "00:00:00" && endTime === "23:59:59") {
          return true;
        }

        const entranceStart = entranceTime.StartTime;
        const entranceEnd = entranceTime.EndTime;
        const entranceHideEnd = entranceTime.HideEndTime || false;

        const startTimeObj = new Date(`2000-01-01T${startTime}`);
        const entranceStartObj = new Date(`2000-01-01T${entranceStart}`);

        if (startTimeObj <= entranceStartObj) {
          return false;
        }

        if (!entranceHideEnd) {
          const endTimeObj = new Date(`2000-01-01T${endTime}`);
          const entranceEndObj = new Date(`2000-01-01T${entranceEnd}`);
          if (endTimeObj >= entranceEndObj) {
            return true;
          }
        }

        return true;
      });

      if (availableTimes.length > 0) {
        filtered.push({
          ...service,
          Availability: availableTimes,
        });
      }
    });

    return filtered;
  };

  const isTimeAvailable = (time: any, service: any) => {
    if (time.Quantity?.Total === 0) {
      return false;
    }

    if (currentStepNr === 1) {
      const maxBookable = Math.min(
        time.Quantity?.Total || 0,
        time.MaxBookableQuantity || Infinity,
      );
      const maxCapacity = maxBookable * (service.NumPax || 1);

      if (maxCapacity < numPaxReq) {
        return false;
      }

      if (service.GenderDistinction) {
        const manAvailable = time.Quantity?.Man || 0;
        const womanAvailable = time.Quantity?.Woman || 0;
        const available =
          manAvailable >= guestsCount.male &&
          womanAvailable >= guestsCount.female;
        if (!available) {
        }
        return available;
      }

      return true;
    } else {
      if (time.Quantity?.Total === 0) {
        return false;
      }
      return true;
    }
  };

  const handleTimeSelect = (time: any, service: any) => {
    if (currentStepNr === 1) {
      setEntranceTime({
        ...time,
        ServiceCode: service.Code,
        ServiceType: service.Type,
        HideEndTime: service.HideEndTime || false,
      });
    } else {
      const step = packageComposition.find((s) => s.StepNr === currentStepNr);
      if (!step) {
        return;
      }

      const stepSelections = Object.values(selectedTimes)
        .filter(Boolean)
        .filter((st) => st.StepNr === currentStepNr);
      const requiredSelections = step.NrRequested || 1;

      const newSelectedTimes = { ...selectedTimes };

      Object.keys(newSelectedTimes).forEach((key) => {
        const index = parseInt(key);
        if (
          newSelectedTimes[index] &&
          newSelectedTimes[index].StepNr === currentStepNr
        ) {
          delete newSelectedTimes[index];
        }
      });

      // Se il servizio ha un solo orario disponibile ma servono N selezioni,
      // replica la voce per soddisfare requiredSelections (l'utente non ha
      // altra scelta che lo stesso orario).
      const availableTimesForService =
        service.Availability?.filter((a: any) => isTimeAvailable(a, service))
          .length || 1;
      const timesToInsert =
        requiredSelections > 1 && availableTimesForService === 1
          ? requiredSelections
          : 1;

      let nextIndex = Object.keys(newSelectedTimes).length;
      for (let i = 0; i < timesToInsert; i++) {
        newSelectedTimes[nextIndex++] = {
          ...time,
          ServiceCode: service.Code,
          ServiceType: service.Type,
          StepNr: currentStepNr,
        };
      }

      setSelectedTimes(newSelectedTimes);

      if (
        (step.StepCategory === "CAT_PACKAGE_STEP_TREATMENTS" ||
          step.StepCategory === "CAT_PACKAGE_STEP_TREATEMENTS") &&
        requiredSelections > 1 &&
        stepSelections.length + 1 < requiredSelections &&
        timesToInsert === 1
      ) {
        setTimeout(() => {
          fetchedStepsRef.current.delete(currentStepNr);
          fetchServicesForStep(currentStepNr);
        }, 100);
      }
    }
  };

  const allStepServicesForCanProceed = availableServices[currentStepNr] || [];
  const selectedServiceCodeForCanProceed =
    selectedServiceForStep[currentStepNr];
  const hasMultipleServicesForCanProceed =
    allStepServicesForCanProceed.length > 1;
  const needsServiceSelectionForCanProceed =
    hasMultipleServicesForCanProceed && !selectedServiceCodeForCanProceed;

  const canProceedToNextStep = () => {
    const step = packageComposition.find((s) => s.StepNr === currentStepNr);
    if (!step) return false;

    if (currentStepNr === 1) {
      return !!entranceTime;
    }

    if (needsServiceSelectionForCanProceed) {
      return false;
    }

    const stepSelections = Object.values(selectedTimes)
      .filter(Boolean)
      .filter((st) => st.StepNr === currentStepNr);
    const requiredSelections = step.NrRequested || 1;
    return stepSelections.length >= requiredSelections;
  };

  const handleNext = () => {
    const maxStepNr =
      packageComposition.length > 0
        ? Math.max(...packageComposition.map((s) => s.StepNr))
        : 0;

    if (currentStepNr < maxStepNr) {
      setDirection(1);
      setCurrentStepNr(currentStepNr + 1);
    } else {
      const finalPackage = determineSelectedPackage();
      const packageData = {
        package: finalPackage,
        entranceTime,
        selectedTimes: Object.values(selectedTimes).filter(Boolean),
      };
      onConfirm(packageData);
    }
  };

  // Set when the user clicks a time slot. The effect below advances once the
  // new selection has rendered, so `handleNext` reads the fresh state. Steps
  // that need several selections only advance when the last one is in.
  const autoAdvanceRef = useRef(false);

  useEffect(() => {
    if (!autoAdvanceRef.current) return;
    autoAdvanceRef.current = false;
    if (!disableConfirm && canProceedToNextStep()) {
      handleNext();
    }
  }, [entranceTime, selectedTimes]);

  const currentStep = packageComposition.find(
    (s) => s.StepNr === currentStepNr,
  );
  // Scelta del servizio dello step: card nell'ordine di WordPress.
  const allStepServices = sortByWpServiceOrder(
    availableServices[currentStepNr] || [],
    (s: any) => [s.Code],
    rootData?.servicesData,
  );
  const selectedServiceCode = selectedServiceForStep[currentStepNr];
  const stepServices = selectedServiceCode
    ? allStepServices.filter((s: any) => s.Code === selectedServiceCode)
    : allStepServices.length === 1
    ? allStepServices
    : [];
  const hasMultipleServices = allStepServices.length > 1;
  const needsServiceSelection = hasMultipleServices && !selectedServiceCode;
  const currentSelections = Object.values(selectedTimes)
    .filter(Boolean)
    .filter((st) => st.StepNr === currentStepNr);
  const requiredSelections = currentStep
    ? (currentStep.NrRequested || 1) * numPaxReq
    : 0;

  if (hasErrors) {
    return (
      <div className="w-full bg-red-400 text-white p-4">
        <div className="flex items-center justify-center gap-2">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span className="text-white text-center block">
            {errorMessage ||
              t("Qualcosa è andato storto! Ricarica la pagina e riprova")}
          </span>
        </div>
      </div>
    );
  }

  if (!currentStep) {
    return (
      <div className="w-full text-center py-4">
        <span>{t("Nessun step disponibile")}</span>
      </div>
    );
  }

  if (isSkippingStep || loading || isLoadingInitialAvailability) {
    return (
      <div className="w-full max-w-3xl mx-auto flex flex-col px-8 py-8">
        <div className="w-full flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-solid border-secondary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  const getDisplayImageAndTitle = () => {
    // La card in alto deve mostrare il PACCHETTO selezionato (es. "My
    // Happy Hour"), non il sub-servizio dello step corrente (es. "Aperitivo"
    // o "Ingresso Day Spa"). Il nome del sub-servizio appare comunque sotto,
    // nel heading "Selezione Orario [Servizio]".
    const pkg = selectedPackage || selectedPackages?.[0];
    if (pkg) {
      const pkgCmsService = getCmsServiceFromList(
        rootData?.servicesData || [],
        pkg.Code || pkg.apiService?.Code,
      );
      const pkgCode = pkg.Code || pkg.apiService?.Code;
      const pkgCodeObj = pkgCmsService?.acf?.codes?.find(
        (c: any) => c.code === pkgCode,
      );
      const image =
        pkgCodeObj?.image?.id || pkgCmsService?.acf?.image?.id || null;
      const title =
        pkgCodeObj?.short_description ||
        pkgCmsService?.acf?.title ||
        pkg.Name ||
        pkg.apiService?.Name ||
        null;
      if (image || title) return { image, title };
    }

    // Fallback al sub-servizio (legacy) se non abbiamo il pacchetto.
    const firstService = stepServices[0];
    if (!firstService) return { image: null, title: null };

    const cmsService = getCmsServiceFromList(
      rootData?.servicesData || [],
      firstService.Code,
    );

    const codeObj = cmsService?.acf?.codes?.find(
      (c: any) => c.code === firstService.Code,
    );

    const cmsServiceCategory = firstService.Category?.[0]
      ? getCmsServiceCategoryFromList(
          rootData?.normalServiceCategories || [],
          firstService.Category[0].code,
        )
      : null;

    const displayImage =
      codeObj?.image?.id ||
      cmsService?.acf?.image?.id ||
      cmsServiceCategory?.acf?.image?.id;

    const displayTitle = cmsService
      ? codeObj?.short_description ||
        cmsService.acf?.title ||
        cmsServiceCategory?.name
      : cmsServiceCategory?.name;

    return { image: displayImage, title: displayTitle };
  };

  const { image: displayImage, title: displayTitle } =
    getDisplayImageAndTitle();

  // Descrizione mostrata nella card in alto. Deve essere SEMPRE la descrizione
  // del relativo CODICE (non quella generica dell'intero servizio):
  //  - Step 1 (ingresso): descrizione del codice del PACCHETTO selezionato.
  //  - Step >= 2: descrizione del codice del sotto-servizio in selezione.
  // `stepServices[0]` è la stessa fonte usata dall'heading, quindi resta
  // coerente con il titolo mostrato.
  const currentStepService = stepServices[0];
  const currentServiceDescription = (() => {
    if (currentStepNr === 1) {
      const pkg = selectedPackage || selectedPackages?.[0];
      if (!pkg) return null;
      const pkgCode = pkg.Code || pkg.apiService?.Code;
      const pkgCmsService = getCmsServiceFromList(
        rootData?.servicesData || [],
        pkgCode,
      );
      const pkgCodeObj = pkgCmsService?.acf?.codes?.find(
        (c: any) => c.code === pkgCode,
      );
      return pkgCodeObj?.long_description || null;
    }

    if (!currentStepService) return null;
    const cmsService = getCmsServiceFromList(
      rootData?.servicesData || [],
      currentStepService.Code,
    );
    const codeObj = cmsService?.acf?.codes?.find(
      (c: any) => c.code === currentStepService.Code,
    );
    return codeObj?.long_description || null;
  })();

  const stepAnimationVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 100 : -100,
      opacity: 0,
    }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({
      x: dir < 0 ? 100 : -100,
      opacity: 0,
    }),
  };

  return (
    <div
      id="step-package"
      className="w-full max-w-3xl mx-auto flex flex-col px-8 py-8"
    >
      <AnimatePresence mode="wait" custom={direction} initial={false}>
        <motion.div
          onUpdate={keepAnimationOnMainThread}
          key={currentStepNr}
          custom={direction}
          variants={stepAnimationVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="w-full flex flex-col"
        >
      {displayImage && displayTitle && (
        <div
          className={cn(
            "mb-6 rounded-2xl overflow-hidden bg-white shadow-md mx-auto w-full max-w-md",
          )}
        >
          <div className="w-full h-60 relative">
            <motion.img
              onUpdate={keepAnimationOnMainThread}
              alt=""
              src={`/cms-asset/${displayImage}`}
              className="w-full h-full object-cover object-center z-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                duration: 0.3,
                delay: 0.5,
                ease: "easeInOut",
              }}
            />
          </div>
          <div className="p-6">
            <h4 className="text-secondary font-bold text-xl leading-none">
              {displayTitle}
            </h4>
            {currentServiceDescription && (
              <div
                className="text-sm text-gray-600 mt-3"
                dangerouslySetInnerHTML={{
                  __html: currentServiceDescription,
                }}
              />
            )}
          </div>
        </div>
      )}
      <h3 className="font-bold text-xl text-center">
        {currentStepNr === 1
          ? t("Selezione Orario Ingresso")
          : (() => {
              const firstService = stepServices[0];
              if (firstService) {
                const cmsService = getCmsServiceFromList(
                  rootData?.servicesData || [],
                  firstService.Code,
                );
                const serviceName =
                  cmsService?.acf?.codes?.find(
                    (c: any) => c.code === firstService.Code,
                  )?.short_description ||
                  cmsService?.acf?.title ||
                  firstService.Name;

                return serviceName || firstService.Code;
              }
              return t("Selezione Trattamento");
            })()}
      </h3>
      <p className="text-center mt-2">
        {needsServiceSelection
          ? t("Seleziona il servizio che desideri")
          : currentStepNr === 1
          ? t("Seleziona l'orario di ingresso per tutti gli ospiti")
          : t("Seleziona l'orario per tutti gli ospiti")}
      </p>

      {isLoadingServices && (
        <div className="w-full flex items-center justify-center py-4">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-solid border-secondary border-t-transparent"></div>
        </div>
      )}

      {!isLoadingServices && (
        <div className="w-full mt-4 space-y-4">
          {needsServiceSelection && (
            <div className="w-full space-y-4">
              <p className="text-center text-gray-600 mb-4">
                {t("Seleziona il servizio")}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {allStepServices.map((service: any, serviceIndex: number) => {
                  const cmsService = getCmsServiceFromList(
                    rootData?.servicesData || [],
                    service.Code,
                  );
                  const codeObj = cmsService?.acf?.codes?.find(
                    (c: any) => c.code === service.Code,
                  );
                  const serviceShortDescription = codeObj?.short_description;
                  const serviceDescription = codeObj?.long_description;
                  const serviceSubtitle = codeObj?.card_subtitle;
                  const serviceName =
                    serviceShortDescription ||
                    cmsService?.acf?.title ||
                    service.Name ||
                    service.Code;
                  const displayImage =
                    codeObj?.image?.id || cmsService?.acf?.image?.id;

                  return (
                    <div
                      key={serviceIndex}
                      className={cn(
                        "w-full rounded-2xl overflow-hidden bg-white ring-2 ring-gray-200 shadow-md cursor-pointer hover:ring-secondary transition-all",
                        {
                          "ring-2 ring-secondary":
                            selectedServiceCode === service.Code,
                        },
                      )}
                      onClick={() => {
                        setSelectedServiceForStep((prev) => ({
                          ...prev,
                          [currentStepNr]: service.Code,
                        }));
                        const existingSelections = Object.values(selectedTimes)
                          .filter(Boolean)
                          .filter((st) => st.StepNr === currentStepNr);
                        if (existingSelections.length > 0) {
                          const newSelectedTimes = { ...selectedTimes };
                          Object.keys(newSelectedTimes).forEach((key) => {
                            const index = parseInt(key);
                            if (
                              newSelectedTimes[index] &&
                              newSelectedTimes[index].StepNr === currentStepNr
                            ) {
                              delete newSelectedTimes[index];
                            }
                          });
                          setSelectedTimes(newSelectedTimes);
                        }
                      }}
                    >
                      {displayImage && (
                        <div className="w-full h-52 relative">
                          <motion.img
                            onUpdate={keepAnimationOnMainThread}
                            alt=""
                            src={`/cms-asset/${displayImage}`}
                            className="w-full h-full object-cover object-center z-10"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{
                              duration: 0.3,
                              delay: serviceIndex * 0.1 + 0.5,
                              ease: "easeInOut",
                            }}
                          />
                        </div>
                      )}
                      <div className="p-6 flex flex-col">
                        <span
                          className="text-lg font-semibold leading-[1.2] mb-2"
                          dangerouslySetInnerHTML={{
                            __html: serviceName,
                          }}
                        />
                        <ServiceCardSubtitle text={serviceSubtitle} />
                        {serviceDescription && (
                          <span
                            className="text-sm text-gray-600 mt-2"
                            dangerouslySetInnerHTML={{
                              __html: serviceDescription,
                            }}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {!needsServiceSelection &&
            stepServices.length === 0 &&
            currentStepNr > 1 && (
              <div className="w-full text-center py-8">
                <p className="text-gray-500">
                  {t("Nessun servizio disponibile")}
                </p>
              </div>
            )}
          {!needsServiceSelection &&
            stepServices.map((service, serviceIndex) => {
              if (!service.Availability || service.Availability.length === 0) {
                return null;
              }

              const cmsService = getCmsServiceFromList(
                rootData?.servicesData || [],
                service.Code,
              );
              const serviceShortDescription = cmsService?.acf?.codes?.find(
                (c: any) => c.code === service.Code,
              )?.short_description;

              return (
                <div key={serviceIndex} className="w-full">
                  <div className="grid grid-cols-2 md:grid-cols-5 xl:grid-cols-5 gap-3">
                    {service.Availability.map(
                      (time: any, timeIndex: number) => {
                        const available = isTimeAvailable(time, service);

                        const isSelected =
                          currentStepNr === 1
                            ? entranceTime?.StartTime === time.StartTime &&
                              entranceTime?.EndTime === time.EndTime
                            : currentSelections.some(
                                (st) =>
                                  st &&
                                  st.StepNr === currentStepNr &&
                                  st.ServiceCode === service.Code &&
                                  st.StartTime === time.StartTime &&
                                  st.EndTime === time.EndTime,
                              );

                        const isDailyService =
                          time.StartTime === "00:00:00" &&
                          time.EndTime === "23:59:59";

                        return (
                          <div
                            key={timeIndex}
                            className={cn(
                              "w-full text-md rounded-2xl overflow-hidden bg-white ring-2 ring-white shadow-md cursor-pointer flex flex-col items-center justify-center px-1 py-1 min-h-12",
                              {
                                "ring-2 ring-secondary":
                                  isSelected && available,
                                "opacity-50 cursor-not-allowed": !available,
                              },
                            )}
                            onClick={() => {
                              if (available) {
                                if (isSelected) {
                                  // Back on a single-choice step whose slot
                                  // is already picked: with the "Avanti"
                                  // button hidden, clicking it again moves on
                                  // instead of clearing it.
                                  const isSingleChoiceStep =
                                    currentStepNr === 1 ||
                                    (currentStep?.NrRequested || 1) === 1;
                                  if (
                                    !SHOW_CONFIRM_ON_AUTO_ADVANCE_STEPS &&
                                    isSingleChoiceStep
                                  ) {
                                    if (
                                      !disableConfirm &&
                                      canProceedToNextStep()
                                    ) {
                                      handleNext();
                                    }
                                    return;
                                  }
                                  if (currentStepNr === 1) {
                                    setEntranceTime(null);
                                  } else {
                                    const newSelectedTimes = {
                                      ...selectedTimes,
                                    };
                                    Object.keys(newSelectedTimes).forEach(
                                      (key) => {
                                        const index = parseInt(key);
                                        if (
                                          newSelectedTimes[index] &&
                                          newSelectedTimes[index].StepNr ===
                                            currentStepNr &&
                                          newSelectedTimes[index]
                                            .ServiceCode === service.Code &&
                                          newSelectedTimes[index].StartTime ===
                                            time.StartTime &&
                                          newSelectedTimes[index].EndTime ===
                                            time.EndTime
                                        ) {
                                          delete newSelectedTimes[index];
                                        }
                                      },
                                    );
                                    setSelectedTimes(newSelectedTimes);
                                  }
                                } else {
                                  autoAdvanceRef.current = true;
                                  handleTimeSelect(time, service);
                                }
                              }
                            }}
                          >
                            <span className="text-center leading-[1.2]">
                              {isDailyService
                                ? t("Servizio Giornaliero")
                                : service.HideEndTime || time.HideEndTime
                                ? time.StartTime.slice(0, 5)
                                : `${time.StartTime.slice(
                                    0,
                                    5,
                                  )} - ${time.EndTime.slice(0, 5)}`}
                            </span>
                            {!available && (
                              <span className="text-xs text-red-500">
                                {t("Esaurito")}
                              </span>
                            )}
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      )}

        </motion.div>
      </AnimatePresence>

      <div className="w-full flex flex-row items-center justify-center space-x-2 mt-4">
        <Button
          variant={"muted"}
          onClick={() => {
            if (currentStepNr === 1) {
              onCancel();
            } else {
              const newStepNr = currentStepNr - 1;
              setDirection(-1);
              setCurrentStepNr(newStepNr);
              setSelectedServiceForStep((prev) => {
                const newState = { ...prev };
                Object.keys(newState).forEach((stepNrStr) => {
                  const stepNr = parseInt(stepNrStr);
                  if (stepNr >= currentStepNr) {
                    delete newState[stepNr];
                  }
                });
                return newState;
              });
              setSelectedTimes((prev) => {
                const newTimes = { ...prev };
                Object.keys(newTimes).forEach((key) => {
                  const index = parseInt(key);
                  if (
                    newTimes[index] &&
                    newTimes[index].StepNr >= currentStepNr
                  ) {
                    delete newTimes[index];
                  }
                });
                return newTimes;
              });
            }
          }}
        >
          <span>{t("Indietro")}</span>
        </Button>
        {SHOW_CONFIRM_ON_AUTO_ADVANCE_STEPS && (
          <Button
            disabled={!canProceedToNextStep() || disableConfirm}
            variant={"secondary"}
            onClick={handleNext}
          >
            <span>
              {(() => {
                const maxStepNr =
                  packageComposition.length > 0
                    ? Math.max(...packageComposition.map((s) => s.StepNr))
                    : 0;
                return currentStepNr < maxStepNr ? t("Avanti") : t("Conferma");
              })()}
            </span>
          </Button>
        )}
      </div>
    </div>
  );
};

export default PackageStep;
